import { useMemo, useState } from 'react'
import {
  BacktestResults,
  BacktestResultsSkeleton,
  EquityCurve,
  LongShortSplit,
  MonthlyReturnsHeatmap,
  PnlView,
  RiskView,
  SummaryMetrics,
  type BacktestResultsView,
  type EntriesByTimeGranularity,
  type EntriesByTimePoint,
  type KpiStatItem,
  type LongShortMetric,
  type MetricEntry,
  type MfeMaeMode,
  type StreakMetric,
  type SummaryMetricsTab,
} from '@/components'
import type {
  BacktestGraphData,
  BacktestResultData,
} from '../api'
import type { EquityPoint, EquityRunningTotals } from '../hooks/useEquityCurve'

const KPI_TONE_FOR_SIGN = (v: number): KpiStatItem['tone'] =>
  v > 0 ? 'success' : v < 0 ? 'danger' : 'neutral'

interface BotBacktestNewResultsProps {
  equityPoints: EquityPoint[]
  equityRunningTotals: EquityRunningTotals | null
  resultsData: BacktestResultData | null
  graphData: BacktestGraphData | null
  isBacktestCompleted: boolean
  initialBalance: number
  // Playback-sync state — keeps the equity curve in step with the candle
  // chart's replay cursor. All optional so the component can also be used
  // outside of a live replay context.
  maxAvailableTs?: number
  equityDisplayBoundaryTs?: number
  currentTimestamp?: number
  rangeStartTs?: number
  rangeEndTs?: number
  onTimeClick?: (time: number) => void
}

interface RunningView {
  balance: number
  equity: number
  totalTrades: number
  winningTrades: number
  losingTrades: number
  grossProfit: number
  grossLoss: number
  commissionPaid: number
  maxDrawdownPct: number
  bestPnl: number
  worstPnl: number
  longCount: number
  shortCount: number
  longWins: number
  shortWins: number
  totalReturn: number
  returnPct: number
  winRate: number
}

/**
 * Collapse running aggregates (live, from equity events) and final results
 * (post-completion, from result_summary) into a single view-model. Final
 * data wins when present; running aggregates fill in while the backtest is
 * still in progress so KPI cards tick live.
 */
function buildRunningView(
  resultsData: BacktestResultData | null,
  running: EquityRunningTotals | null,
  initialBalance: number,
): RunningView {
  const balance = resultsData?.final_balance ?? running?.balance ?? initialBalance
  const equity = running?.equity ?? balance
  const totalTrades = resultsData?.total_trades ?? running?.totalTrades ?? 0
  const winningTrades = resultsData?.winning_trades ?? running?.winningTrades ?? 0
  const losingTrades = resultsData?.losing_trades ?? running?.losingTrades ?? 0
  const grossProfit = resultsData?.gross_profit ?? running?.grossProfit ?? 0
  const grossLoss = resultsData?.gross_loss ?? running?.grossLoss ?? 0
  const commissionPaid =
    resultsData?.total_commission_paid ?? running?.totalCommissionPaid ?? 0
  const maxDrawdownPct = resultsData?.max_drawdown ?? running?.maxDrawdownPct ?? 0
  const bestPnl = resultsData?.best_pnl ?? running?.bestPnl ?? 0
  const worstPnl = resultsData?.worst_pnl ?? running?.worstPnl ?? 0
  const longCount = resultsData?.long_count ?? running?.longCount ?? 0
  const shortCount = resultsData?.short_count ?? running?.shortCount ?? 0
  const longWins = resultsData?.long_wins ?? running?.longWins ?? 0
  const shortWins = resultsData?.short_wins ?? running?.shortWins ?? 0
  const totalReturn = resultsData?.total_return ?? (balance - initialBalance)
  const returnPct =
    resultsData?.return_percentage
    ?? (initialBalance > 0 ? (balance / initialBalance - 1) * 100 : 0)
  const winRate =
    resultsData?.win_rate
    ?? (totalTrades > 0 ? (winningTrades / totalTrades) * 100 : 0)

  return {
    balance, equity,
    totalTrades, winningTrades, losingTrades,
    grossProfit, grossLoss, commissionPaid,
    maxDrawdownPct,
    bestPnl, worstPnl,
    longCount, shortCount, longWins, shortWins,
    totalReturn, returnPct, winRate,
  }
}

function fmtMoney(v: number, opts?: { sign?: boolean; decimals?: number }): string {
  const decimals = opts?.decimals ?? 2
  const abs = Math.abs(v).toFixed(decimals)
  if (opts?.sign && v >= 0) return `+$${abs}`
  if (v < 0) return `-$${abs}`
  return `$${abs}`
}

function fmtPct(v: number, opts?: { sign?: boolean; decimals?: number }): string {
  const decimals = opts?.decimals ?? 1
  if (opts?.sign && v >= 0) return `+${v.toFixed(decimals)}%`
  return `${v.toFixed(decimals)}%`
}

function shortDateLabel(unixSeconds: number): string {
  const d = new Date(unixSeconds * 1000)
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(d.getUTCDate()).padStart(2, '0')
  return `${mm}/${dd}`
}

function buildEquityData(
  equityPoints: EquityPoint[],
  initialBalance: number,
) {
  return equityPoints.map((p) => ({
    time: p.time,
    label: shortDateLabel(p.time),
    realised: p.balance - initialBalance,
    equity: p.equity - initialBalance,
  }))
}

/**
 * Build the running max-drawdown series from the live equity stream so the
 * RiskView's Balance & Drawdown chart ticks live as new points arrive.
 */
function buildBalanceDrawdown(equityPoints: EquityPoint[]) {
  if (equityPoints.length === 0) return []
  let runningMax = equityPoints[0].equity
  return equityPoints.map((p) => {
    if (p.equity > runningMax) runningMax = p.equity
    const drawdown = runningMax > 0
      ? -((runningMax - p.equity) / runningMax) * 100
      : 0
    return {
      time: p.time,
      label: shortDateLabel(p.time),
      balance: Number(p.balance.toFixed(2)),
      drawdown: Number(drawdown.toFixed(2)),
    }
  })
}

function buildSummaryTabs(
  view: RunningView,
  resultsData: BacktestResultData | null,
): SummaryMetricsTab[] {
  const avgWin = resultsData?.avg_win ?? 0
  const avgLoss = resultsData?.avg_loss ?? 0
  const profitFactor = resultsData?.profit_factor ?? (
    view.grossLoss > 0 ? view.grossProfit / view.grossLoss : view.grossProfit > 0 ? 999.99 : 0
  )
  const sharpe = resultsData?.sharpe_ratio ?? 0
  const sortino = resultsData?.sortino_ratio ?? 0
  const expectancy = resultsData?.expectancy ?? (
    view.totalTrades > 0 ? view.totalReturn / view.totalTrades : 0
  )
  const recoveryFactor = resultsData?.recovery_factor ?? (
    view.maxDrawdownPct > 0 ? view.returnPct / view.maxDrawdownPct : 0
  )
  const avgHoldMinutes = resultsData?.avg_hold_minutes ?? 0
  const avgHoldHrs = avgHoldMinutes / 60
  const longsWonPct = view.longCount > 0 ? (view.longWins / view.longCount) * 100 : 0
  const shortsWonPct = view.shortCount > 0 ? (view.shortWins / view.shortCount) * 100 : 0

  const overview: MetricEntry[] = [
    { label: 'Profit Factor', value: profitFactor.toFixed(2) },
    { label: 'Gross Profit', value: fmtMoney(view.grossProfit, { sign: true }), tone: 'success' },
    { label: 'Sharpe Ratio', value: sharpe.toFixed(2) },
    { label: 'Gross Loss', value: fmtMoney(-Math.abs(view.grossLoss)), tone: 'danger' },
    { label: 'Max Drawdown', value: fmtPct(view.maxDrawdownPct), tone: 'danger' },
    { label: 'Best Trade', value: fmtMoney(view.bestPnl, { sign: true }), tone: 'success' },
    { label: 'Expectancy', value: fmtMoney(expectancy) },
    { label: 'Avg Win', value: fmtMoney(avgWin, { sign: true }), tone: 'success' },
    { label: 'Win Rate', value: fmtPct(view.winRate) },
    { label: 'Avg Loss', value: fmtMoney(-Math.abs(avgLoss)), tone: 'danger' },
    { label: 'Win / Loss', value: `${view.winningTrades} / ${view.losingTrades}` },
    { label: 'Avg Hold', value: avgHoldHrs > 0 ? `${avgHoldHrs.toFixed(1)}h` : '—' },
  ]

  const trades: MetricEntry[] = [
    { label: 'Total Trades', value: `${view.totalTrades}` },
    { label: 'Longs Won', value: `${view.longWins}/${view.longCount} \u00B7 ${longsWonPct.toFixed(0)}%` },
    { label: 'Long Trades', value: `${view.longCount}` },
    { label: 'Shorts Won', value: `${view.shortWins}/${view.shortCount} \u00B7 ${shortsWonPct.toFixed(0)}%` },
    { label: 'Short Trades', value: `${view.shortCount}` },
    { label: 'Best Trade', value: fmtMoney(view.bestPnl, { sign: true }), tone: 'success' },
    { label: 'Winning', value: `${view.winningTrades}`, tone: 'success' },
    { label: 'Worst Trade', value: fmtMoney(-Math.abs(view.worstPnl)), tone: 'danger' },
    { label: 'Losing', value: `${view.losingTrades}`, tone: 'danger' },
    { label: 'Avg Hold', value: avgHoldHrs > 0 ? `${avgHoldHrs.toFixed(1)}h` : '—' },
  ]

  const riskRewardRatio = Math.abs(avgLoss) > 0 ? Math.abs(avgWin / avgLoss) : 0
  const risk: MetricEntry[] = [
    { label: 'Profit Factor', value: profitFactor.toFixed(2) },
    { label: 'Recovery Factor', value: recoveryFactor.toFixed(2) },
    { label: 'Sharpe Ratio', value: sharpe.toFixed(2) },
    { label: 'Sortino Ratio', value: sortino.toFixed(2) },
    { label: 'Gross Profit', value: fmtMoney(view.grossProfit, { sign: true }), tone: 'success' },
    { label: 'Max Drawdown', value: fmtPct(view.maxDrawdownPct), tone: 'danger' },
    { label: 'Gross Loss', value: fmtMoney(-Math.abs(view.grossLoss)), tone: 'danger' },
    { label: 'Expectancy', value: fmtMoney(expectancy) },
    { label: 'Avg Win PnL', value: fmtMoney(avgWin, { sign: true }), tone: 'success' },
    { label: 'Risk/Reward', value: riskRewardRatio.toFixed(2) },
    { label: 'Avg Loss PnL', value: fmtMoney(-Math.abs(avgLoss)), tone: 'danger' },
  ]

  return [
    { id: 'overview', label: 'Overview', metrics: overview },
    { id: 'trades', label: 'Trades', metrics: trades },
    { id: 'risk', label: 'Risk', metrics: risk },
  ]
}

/**
 * Build session-bucketed Entries-by-Time granularities from graph data.
 *
 * When the backend provides per-trading-session counts (`bucket.sessions`),
 * we use them directly so Hourly, Weekly, and Monthly all stack Asia /
 * Europe / USA accurately. For legacy backtests where `sessions` is
 * missing, we fall back to the hour-band heuristic (Asia: hour<8, Europe:
 * 8..15, USA: 16+) for Hourly, and to a single Europe bucket for Weekday
 * and Month (no hour info to derive a region split) so the chart still
 * renders.
 */
function buildEntriesGranularities(
  graphData: BacktestGraphData | null,
): EntriesByTimeGranularity[] {
  if (!graphData) return []

  const hourly: EntriesByTimePoint[] = []
  for (let h = 0; h < 24; h++) {
    const key = String(h)
    const bucket = graphData.hourly?.[key]
    const label = `${h.toString().padStart(2, '0')}:00`
    const point: EntriesByTimePoint = { label }
    if (bucket?.sessions) {
      point.asia = bucket.sessions.asia
      point.europe = bucket.sessions.europe
      point.usa = bucket.sessions.usa
    } else {
      const total = bucket?.entries ?? 0
      if (h < 8) point.asia = total
      else if (h < 16) point.europe = total
      else point.usa = total
    }
    hourly.push(point)
  }

  const weekdays: EntriesByTimePoint[] = ['1', '2', '3', '4', '5', '6', '7']
    .map((d) => ({ d, label: ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][Number(d)] }))
    .filter(({ d }) => graphData.weekdays?.[d])
    .map(({ d, label }) => {
      const bucket = graphData.weekdays?.[d]
      if (bucket?.sessions) {
        return {
          label,
          asia: bucket.sessions.asia,
          europe: bucket.sessions.europe,
          usa: bucket.sessions.usa,
        }
      }
      return { label, europe: bucket?.entries ?? 0 }
    })

  const months: EntriesByTimePoint[] = []
  const MONTH_LABEL = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  for (let m = 1; m <= 12; m++) {
    const key = m.toString().padStart(2, '0')
    const bucket = graphData.months?.[key]
    if (!bucket) continue
    if (bucket.sessions) {
      months.push({
        label: MONTH_LABEL[m],
        asia: bucket.sessions.asia,
        europe: bucket.sessions.europe,
        usa: bucket.sessions.usa,
      })
    } else {
      months.push({
        label: MONTH_LABEL[m],
        europe: bucket.entries,
      })
    }
  }

  return [
    { id: 'hourly', label: 'Hourly', data: hourly },
    { id: 'weekly', label: 'Weekly', data: weekdays },
    { id: 'monthly', label: 'Monthly', data: months },
  ]
}

function buildLongShortMetrics(
  view: RunningView,
  resultsData: BacktestResultData | null,
): LongShortMetric[] {
  const longLosses = resultsData?.long_losses ?? Math.max(0, view.longCount - view.longWins)
  const shortLosses = resultsData?.short_losses ?? Math.max(0, view.shortCount - view.shortWins)
  const longGrossProfit = resultsData?.long_gross_profit ?? 0
  const shortGrossProfit = resultsData?.short_gross_profit ?? 0
  const longGrossLoss = resultsData?.long_gross_loss ?? 0
  const shortGrossLoss = resultsData?.short_gross_loss ?? 0
  const longNetPnl = resultsData?.long_net_pnl ?? (longGrossProfit - longGrossLoss)
  const shortNetPnl = resultsData?.short_net_pnl ?? (shortGrossProfit - shortGrossLoss)
  const longWinRate = resultsData?.long_win_rate ?? (
    view.longCount > 0 ? (view.longWins / view.longCount) * 100 : 0
  )
  const shortWinRate = resultsData?.short_win_rate ?? (
    view.shortCount > 0 ? (view.shortWins / view.shortCount) * 100 : 0
  )
  const longPf = resultsData?.long_profit_factor ?? (
    longGrossLoss > 0 ? longGrossProfit / longGrossLoss : longGrossProfit > 0 ? 999 : 0
  )
  const shortPf = resultsData?.short_profit_factor ?? (
    shortGrossLoss > 0 ? shortGrossProfit / shortGrossLoss : shortGrossProfit > 0 ? 999 : 0
  )
  const avgLongWin = resultsData?.avg_long_win ?? 0
  const avgShortWin = resultsData?.avg_short_win ?? 0
  const avgLongLoss = resultsData?.avg_long_loss ?? 0
  const avgShortLoss = resultsData?.avg_short_loss ?? 0

  return [
    { label: 'Total Trades', long: view.longCount, short: view.shortCount, format: 'number' },
    { label: 'Win Rate', long: longWinRate, short: shortWinRate, format: 'pct' },
    { label: 'Net P&L', long: longNetPnl, short: shortNetPnl, format: 'money' },
    { label: 'Gross Profit', long: longGrossProfit, short: shortGrossProfit, format: 'money' },
    { label: 'Gross Loss', long: longGrossLoss, short: shortGrossLoss, format: 'money' },
    { label: 'Losing Trades', long: longLosses, short: shortLosses, format: 'number' },
    { label: 'Profit Factor', long: longPf, short: shortPf, format: 'ratio' },
    { label: 'Avg Win', long: avgLongWin, short: avgShortWin, format: 'money' },
    { label: 'Avg Loss', long: Math.abs(avgLongLoss), short: Math.abs(avgShortLoss), format: 'money' },
  ]
}

function buildStreakMetrics(resultsData: BacktestResultData | null): StreakMetric[] {
  const bestPnl = resultsData?.best_pnl ?? 0
  const worstPnl = resultsData?.worst_pnl ?? 0
  const maxWins = resultsData?.max_consec_wins ?? 0
  const maxLosses = resultsData?.max_consec_losses ?? 0
  const maxProfit = resultsData?.max_consec_profit ?? 0
  const maxLoss = resultsData?.max_consec_loss ?? 0
  return [
    { label: 'Best / Worst Trade', type: 'money', positive: bestPnl, negative: worstPnl },
    { label: 'Max Consec. Wins / Losses', type: 'count', positive: maxWins, negative: -maxLosses },
    { label: 'Max Consec. Profit / Loss', type: 'money', positive: maxProfit, negative: maxLoss },
  ]
}

function buildMfeMaeModes(
  graphData: BacktestGraphData | null,
  initialBalance: number,
): MfeMaeMode[] {
  const source = graphData?.mfe_mae ?? []
  if (source.length === 0) return []
  const money = source.map((d) => ({
    trade: d.trade,
    mfe: d.mfe,
    mae: d.mae,
    avgProfit: d.avgProfit,
    avgLoss: d.avgLoss,
  }))
  const pct = source.map((d) => ({
    trade: d.trade,
    mfe: initialBalance > 0 ? Number(((d.mfe / initialBalance) * 100).toFixed(2)) : 0,
    mae: initialBalance > 0 ? Number(((d.mae / initialBalance) * 100).toFixed(2)) : 0,
    avgProfit: initialBalance > 0 ? Number(((d.avgProfit / initialBalance) * 100).toFixed(2)) : 0,
    avgLoss: initialBalance > 0 ? Number(((d.avgLoss / initialBalance) * 100).toFixed(2)) : 0,
  }))
  return [
    { id: 'money', label: 'Money', data: money, format: 'money' },
    { id: 'pct', label: '%', data: pct, format: 'pct' },
  ]
}

function PendingPlaceholder({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-b border-border px-4 py-6 text-caption text-muted-foreground">
      {children}
    </div>
  )
}

export default function BotBacktestNewResults({
  equityPoints,
  equityRunningTotals,
  resultsData,
  graphData,
  isBacktestCompleted,
  initialBalance,
  maxAvailableTs,
  equityDisplayBoundaryTs,
  currentTimestamp,
  rangeStartTs,
  rangeEndTs,
  onTimeClick,
}: BotBacktestNewResultsProps) {
  const [activeViewId, setActiveViewId] = useState<string>('summary')

  const view = useMemo(
    () => buildRunningView(resultsData, equityRunningTotals, initialBalance),
    [resultsData, equityRunningTotals, initialBalance],
  )

  const equityData = useMemo(
    () => buildEquityData(equityPoints, initialBalance),
    [equityPoints, initialBalance],
  )

  // Effective playback boundary: the cursor capped by how far the backtest
  // has actually streamed data.
  const equityBoundaryTime = useMemo(() => {
    if (maxAvailableTs != null && equityDisplayBoundaryTs != null) {
      return Math.min(maxAvailableTs, equityDisplayBoundaryTs)
    }
    return maxAvailableTs ?? equityDisplayBoundaryTs
  }, [maxAvailableTs, equityDisplayBoundaryTs])
  const balanceDrawdownData = useMemo(
    () => buildBalanceDrawdown(equityPoints),
    [equityPoints],
  )
  const summaryTabs = useMemo(
    () => buildSummaryTabs(view, resultsData),
    [view, resultsData],
  )
  const entriesGranularities = useMemo(
    () => buildEntriesGranularities(graphData),
    [graphData],
  )
  const longShortMetrics = useMemo(
    () => buildLongShortMetrics(view, resultsData),
    [view, resultsData],
  )
  const streakMetrics = useMemo(
    () => buildStreakMetrics(resultsData),
    [resultsData],
  )
  const mfeMaeModes = useMemo(
    () => buildMfeMaeModes(graphData, initialBalance),
    [graphData, initialBalance],
  )

  const monthlyPnlData = graphData?.monthly_pnl ?? []
  const dayOfWeekData = graphData?.day_of_week ?? []
  const yearlyReturns = graphData?.yearly_returns ?? []

  // No values until the run completes.
  if (!isBacktestCompleted) {
    return <BacktestResultsSkeleton promptMessage="Results will appear here as the backtest runs." />
  }

  const resultKpis: KpiStatItem[] = [
    {
      id: 'summary',
      label: 'RETURN',
      value: fmtPct(view.returnPct, { sign: true }),
      tone: KPI_TONE_FOR_SIGN(view.returnPct),
    },
    {
      id: 'pnl',
      label: 'NET PROFIT',
      value: fmtMoney(view.totalReturn, { sign: true, decimals: 0 }),
      tone: KPI_TONE_FOR_SIGN(view.totalReturn),
    },
    {
      id: 'winrate',
      label: 'WIN RATE',
      value: fmtPct(view.winRate),
      tone: 'neutral',
    },
    {
      id: 'maxdd',
      label: 'MAX DD',
      value: `\u2212${Math.abs(view.maxDrawdownPct).toFixed(1)}%`,
      tone: 'danger',
    },
  ]

  const longShortSummary = {
    longs: view.longCount,
    shorts: view.shortCount,
    longsWonPct: view.longCount > 0 ? (view.longWins / view.longCount) * 100 : 0,
    shortsWonPct: view.shortCount > 0 ? (view.shortWins / view.shortCount) * 100 : 0,
  }

  const pnlKpis = [
    {
      label: 'Gross Profit',
      value: fmtMoney(view.grossProfit, { sign: true, decimals: 0 }),
      tone: 'success' as const,
    },
    {
      label: 'Gross Loss',
      value: fmtMoney(-Math.abs(view.grossLoss), { decimals: 0 }),
      tone: 'danger' as const,
    },
    {
      label: 'Commissions',
      value: fmtMoney(-Math.abs(view.commissionPaid), { decimals: 0 }),
      tone: 'neutral' as const,
    },
  ]

  const pnlSeriesOptions = monthlyPnlData.length > 0
    ? [{
        id: 'monthly',
        label: 'Monthly',
        data: monthlyPnlData,
        yFormat: 'money' as const,
      }]
    : []

  const summaryContent = (
    <>
      <EquityCurve
        data={equityData}
        initialBalance={initialBalance}
        boundaryTime={equityBoundaryTime}
        currentTime={currentTimestamp}
        rangeStartTs={rangeStartTs}
        rangeEndTs={rangeEndTs}
        onTimeClick={onTimeClick}
      />
      <SummaryMetrics tabs={summaryTabs} />
      {isBacktestCompleted && yearlyReturns.length > 0 ? (
        <MonthlyReturnsHeatmap years={yearlyReturns} />
      ) : (
        <PendingPlaceholder>
          Monthly returns heatmap available when the backtest completes.
        </PendingPlaceholder>
      )}
    </>
  )

  const pnlContent = (
    <>
      {pnlSeriesOptions.length > 0 ? (
        <PnlView
          kpis={pnlKpis}
          seriesOptions={pnlSeriesOptions}
          dayOfWeek={dayOfWeekData.length > 0 ? dayOfWeekData : undefined}
        />
      ) : (
        <>
          <PnlView
            kpis={pnlKpis}
            seriesOptions={[]}
          />
          <PendingPlaceholder>
            Detailed P&amp;L is available when the backtest completes.
          </PendingPlaceholder>
        </>
      )}
    </>
  )

  const winrateContent = (
    <LongShortSplit
      summary={longShortSummary}
      metrics={longShortMetrics}
      granularities={isBacktestCompleted && entriesGranularities.length > 0
        ? entriesGranularities
        : undefined}
    />
  )

  const maxddContent = (
    <RiskView
      balance={balanceDrawdownData}
      streaks={streakMetrics}
      mfeMaeModes={mfeMaeModes.length > 0 ? mfeMaeModes : EMPTY_MFE_MAE_MODES}
    />
  )

  const resultViews: BacktestResultsView[] = [
    { id: 'summary',  kpi: resultKpis[0], content: summaryContent  },
    { id: 'pnl',      kpi: resultKpis[1], content: pnlContent      },
    { id: 'winrate',  kpi: resultKpis[2], content: winrateContent  },
    { id: 'maxdd',    kpi: resultKpis[3], content: maxddContent    },
  ]

  return (
    <BacktestResults
      views={resultViews}
      activeViewId={activeViewId}
      onViewChange={setActiveViewId}
    />
  )
}

// RiskView requires at least one mfeMaeMode to render its chart. Providing
// an empty-data mode keeps the section visible (with the "no data yet"
// behaviour the chart renders for empty arrays) while the backtest runs.
const EMPTY_MFE_MAE_MODES: MfeMaeMode[] = [
  { id: 'money', label: 'Money', data: [], format: 'money' },
  { id: 'pct', label: '%', data: [], format: 'pct' },
]
